import React, { useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'

import { Input, Select, Textarea, ErrorMessage } from '@baseV2'
import { TOAST_TYPE } from '@base'
import { Spacings } from '@styles'
import { getAddressListFromBoth } from '@data'
import { Address } from '@data/model'
import { strings } from '@constants'
import { showToast } from '@data/state/action'

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

const AddressWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	opacity: ${({ show }) => (show ? 1 : 0)};
	visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
	transition: all 0.2s ease-in-out;
`

const RequiredMark = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`

const initialAddressState = {
	city: '',
	district: '',
	state: '',
	street_address: '',
	country: '',
}

const initialSearchAddressState = {
	value: '',
	valid: false,
}

const AddAddress = ({ addressCb }) => {
	const dispatch = useDispatch()
	const completeAddress = useRef()
	const timeoutId = useRef()
	const [loading, setLoading] = useState(false)
	const [addressList, setAddressList] = useState([])
	const [searchAddress, setSearchAddress] = useState(initialSearchAddressState)
	const [address, setAddress] = useState(initialAddressState)
	const [apiError, setApiError] = useState('')
	const [streetTouched, setStreetTouched] = useState(false)

	const enableButton = useMemo(() => {
		return Boolean(
			searchAddress.value && searchAddress.value.toString().length >= 4 && !apiError && !loading && address?.street_address && address?.pincode
		)
	}, [loading, apiError, searchAddress, address])

	const searchAddressChangeHandler = (key) => (value) => {
		if (apiError) {
			setApiError(false)
		}
		setSearchAddress((val) => ({ ...val, [key]: String(value) }))
		setAddressList([])
	}

	const streetAddressChangeHandler = (value) => {
		setAddress((val) => ({
			...val,
			street_address: value,
		}))
	}

	useEffect(() => {
		if (completeAddress.current) {
			completeAddress.current.complete_address = address?.street_address?.trim()
		}
		addressCb({
			address: completeAddress.current,
			enableButton,
		})
	}, [address, enableButton])

	useEffect(() => {
		clearTimeout(timeoutId.current)
		if (searchAddress.value && searchAddress.value.toString().length >= 4 && !loading) {
			timeoutId.current = setTimeout(getAddresses, 2000)
		}
		return () => {
			setAddress(initialAddressState)
			clearTimeout(timeoutId.current)
		}
	}, [searchAddress])

	const getAddresses = () => {
		clearTimeout(timeoutId.current)
		setLoading(true)
		getAddressListFromBoth(searchAddress.value)
			.then((response) => {
				if (!response || response?.length <= 0) {
					dispatch(showToast(true, 'No address found!', { type: TOAST_TYPE.ERROR }))
				} else {
					setAddressList(response)
					completeAddress.current = new Address(response?.[0])
					setAddress(response?.[0])
				}
			})
			.catch((error) => {
				dispatch(showToast(true, 'No address found!', { type: TOAST_TYPE.ERROR }))
				console.log(error)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const addressSelectHandler = (val) => {
		completeAddress.current = new Address(val)
		setAddress(val)
	}

	const handlePinCodeChange = (val) => {
		setAddress((_address) => ({
			..._address,
			pincode: val,
		}))
		completeAddress.current.pincode = val
	}

	const handleAddressChange = (key) => (value) => {
		setAddress((_address) => ({
			..._address,
			[key]: value,
		}))
		completeAddress.current[key] = value
	}

	const streetError = streetTouched && !address.street_address ? 'Complete address is required' : ''

	return (
		<Container>
			<Input
				label={strings('search', 'address')}
				placeholder={'Search by pincode / city / state / address'}
				id='search-address'
				disabled={loading}
				value={searchAddress.value ?? ''}
				onChange={(e) => searchAddressChangeHandler('value')(e.target.value)}
			/>
			<AddressWrapper show={address && addressList.length > 0}>
				<Select
					id='select-address'
					label={strings('select', 'address')}
					data={addressList}
					primaryKey={'id'}
					displayKey={'complete_address'}
					value={addressList.find((item) => item.id === address.id) ?? {}}
					onChange={addressSelectHandler}
				/>
			</AddressWrapper>
			{apiError && <ErrorMessage>{apiError}</ErrorMessage>}
			<AddressWrapper show={address.city && address.state}>
				<Input
					label={strings('pincode')}
					id='pincode'
					min={1000}
					max={99999999}
					type='number'
					disabled={loading}
					value={address.pincode ?? ''}
					onChange={(e) => {
						const val = e.target.value
						handlePinCodeChange(val === '' ? '' : Math.abs(val))
					}}
				/>
				<Input
					label={strings('city')}
					id='city'
					type='text'
					value={address.city}
					onChange={(e) => handleAddressChange('city')(e.target.value)}
				/>
				<Input
					label={strings('district')}
					id='district'
					type='text'
					value={address.district}
					onChange={(e) => handleAddressChange('district')(e.target.value)}
				/>
				<Input
					label={strings('state')}
					id='state'
					type='text'
					value={address.state}
					onChange={(e) => handleAddressChange('state')(e.target.value)}
				/>
				<Input label={strings('country')} id='country' type='text' value={address.country} disabled />
				<Textarea
					label={
						<>
							{strings('complete', 'address')} <RequiredMark>*</RequiredMark>
						</>
					}
					id='street_address'
					rows={2}
					required
					placeholder={'Enter address'}
					value={address.street_address}
					error={streetError}
					onChange={(e) => streetAddressChangeHandler(e.target.value)}
					onBlur={() => setStreetTouched(true)}
				/>
			</AddressWrapper>
		</Container>
	)
}

export default AddAddress
